import { Component, effect, inject, model, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { faClose, faFolderBookmark, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { BehaviorSubject, of, switchMap, tap } from 'rxjs';

import { NotificationLevel, NotificationsService } from '@eiq/notifications';
import { Option } from '@eiq/ui';
import { QuestionType } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { QuestionTypeConfig } from '../../models/question-type-config.constants';
import { QuestionsLibraryService } from '../../services/questions-library.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'empathy-question-library',
  templateUrl: 'question-library.component.html',
  styleUrls: ['./question-library.component.scss']
})
export class QuestionLibraryComponent {
  selectedQuestions = output<Question[]>();

  backlogOfQuestions$ = signal<Question[]>(null);
  fetchLibrary$ = new BehaviorSubject<{ type: QuestionType; terms: string }>({ type: null, terms: '' });
  filterCtrl = model<string>('', {});
  typeCtrl = model<QuestionType>(null);
  selectedQuestionIndex$ = signal<number[]>([]);
  questionTypesToFilter$ = signal<Option<QuestionType>[]>([]);
  questionsOnLibrary$ = toSignal(
    this.fetchLibrary$.pipe(
      switchMap((filter) => {
        this.selectedQuestionIndex$.set([]);
        if (!filter.terms && !filter.type) {
          return this.questionsLibraryService.fetchLibrary().pipe(
            tap((questions) => {
              const typesList = questions.map((question) => question.type).map((type) => ({ value: type, text: QuestionTypeConfig[type].text }));
              const uniqueTypesList = [];
              typesList.forEach((type) => {
                if (!uniqueTypesList.some((t) => t.value === type.value)) {
                  uniqueTypesList.push(type);
                }
              });

              this.questionTypesToFilter$.set(Array.from(uniqueTypesList));
              this.backlogOfQuestions$.set(questions);
            })
          );
        }
        return of(this.filter(filter.type, filter.terms));
      })
    ),
    { initialValue: [] }
  );

  faClose = faClose;
  faFolderBookmark = faFolderBookmark;
  faMagnifyingGlass = faMagnifyingGlass;
  questionTypeConfig = QuestionTypeConfig;

  private dialog = inject(MatDialog);
  private notificationService = inject(NotificationsService);

  constructor(private questionsLibraryService: QuestionsLibraryService) {
    effect(() => this.fetchLibrary$.next({ type: this.typeCtrl(), terms: this.filterCtrl() }), { allowSignalWrites: true });
    effect(() => this.selectedQuestions.emit(this.selectedQuestionIndex$().map((index) => this.questionsOnLibrary$()[index])));
  }

  onChecked(event: MatCheckboxChange, index: number): void {
    if (event.checked) {
      this.selectedQuestionIndex$.update((selected) => [...selected, index]);
    } else {
      this.selectedQuestionIndex$.update((selected) => selected.filter((i) => i !== index));
    }
  }

  onDeleteQuestionFromLibrary(id: string): void {
    const confirmationRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove question',
        confirmText: 'Are you sure you want to remove this question from the library? The question cannot be recovered after this action.',
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });

    confirmationRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.questionsLibraryService
          .deleteQuestion(id)
          .pipe(tap(() => this.fetchLibrary$.next({ type: this.typeCtrl(), terms: this.filterCtrl() })))
          .subscribe((res) => {
            if (res) {
              this.notificationService.open({
                level: NotificationLevel.Success,
                title: 'Deleted',
                message: 'Question was deleted!'
              });
            } else {
              this.notificationService.open({
                level: NotificationLevel.Error,
                title: 'Error',
                message: 'There was an error deleting the question from the library.'
              });
            }
          });
      }
    });
  }

  private filter(type: QuestionType, terms: string): Question[] {
    return this.backlogOfQuestions$().filter(
      (question) =>
        (!type || question.type === type) &&
        (!terms || question.title.toLowerCase().includes(terms.toLowerCase()) || question.startingQuestion?.toLowerCase().includes(terms.toLowerCase()))
    );
  }
}
